import dayjs, { type Dayjs } from 'dayjs';
import { isOrderDraftPrepaymentPercentValid, type OrderDraftState } from './orderDraftModel';
import { DEFAULT_PREPAYMENT_PERCENT, formatPrepaymentPercent } from '../../orderPaymentMode';

/** Who may see which header field (same switches as `OrderDraftMoreDataRights`). */
export interface OrderDraftMoreSummaryContext {
  isNew: boolean;
  commercial: boolean;
  accountManager: boolean;
  customerPayments: boolean;
  deliveryMode: boolean;
  /** Injectable for tests. */
  now?: Dayjs;
}

/** One marker of the collapsed „Więcej danych” header; `key` is an `orders:` i18n key. */
export interface OrderDraftMoreSummaryToken {
  id: string;
  key: string;
  params?: Record<string, string | number>;
}

const MAX_VALUE_LENGTH = 24;

const shorten = (value: string) => (value.length > MAX_VALUE_LENGTH ? `${value.slice(0, MAX_VALUE_LENGTH - 1)}…` : value);

/** DD.MM; the year only when it is not the current one. */
const shortDate = (value: Dayjs, now: Dayjs) => value.format(value.year() === now.year() ? 'DD.MM' : 'DD.MM.YYYY');

const SUMMARY = 'orders:process.draft.more.summary';

/**
 * The values set in „Więcej danych”, as short markers for the collapsed
 * header, so the operator sees what is filled in without opening it. Only
 * fields the role can see and that carry a value; defaults that are not a
 * choice (automatic numbering, today's order date) are not listed. Nothing
 * here changes what is saved — the fields stay in the section.
 */
export function getOrderDraftMoreSummary(
  draft: OrderDraftState,
  context: OrderDraftMoreSummaryContext,
): OrderDraftMoreSummaryToken[] {
  const { header } = draft;
  const now = context.now ?? dayjs();
  const tokens: OrderDraftMoreSummaryToken[] = [];
  const add = (id: string, key: string, params?: Record<string, string | number>) => tokens.push({ id, key, params });

  if (context.deliveryMode) add('deliveryMode', `orders:deliveryMode.options.${header.deliveryMode}`);
  // „Płatność”: przelew z terminem is the default, not a choice worth a marker.
  // An invalid share is not shown as agreed: the field itself carries the error.
  if (header.paymentMode === 'PREPAYMENT' && isOrderDraftPrepaymentPercentValid(header)) {
    add('paymentMode', `${SUMMARY}.prepayment`, { percent: formatPrepaymentPercent(header.prepaymentPercent ?? DEFAULT_PREPAYMENT_PERCENT) });
  } else if (header.paymentMode === 'FACTORING') {
    add('paymentMode', 'orders:paymentMode.options.FACTORING');
  }
  const clientOrderNumber = header.clientOrderNumber.trim();
  if (clientOrderNumber) add('clientOrderNumber', `${SUMMARY}.clientOrderNumber`, { value: shorten(clientOrderNumber) });
  if (header.expectedDelivery?.isValid()) add('expectedDelivery', `${SUMMARY}.expectedDelivery`, { date: shortDate(header.expectedDelivery, now) });
  if (context.customerPayments && header.customerPaymentDueDate?.isValid()) {
    add('customerPaymentDueDate', `${SUMMARY}.customerPaymentDueDate`, { date: shortDate(header.customerPaymentDueDate, now) });
  }
  if (header.notes.trim()) add('notes', `${SUMMARY}.notes`);

  // Rarer fields (second level „Zaawansowane”).
  if (context.commercial && header.orderDate?.isValid() && !header.orderDate.isSame(now, 'day')) {
    add('orderDate', `${SUMMARY}.orderDate`, { date: shortDate(header.orderDate, now) });
  }
  if (context.commercial && header.useManualNumbering) {
    const manual = header.manualOrderNumber.trim();
    add('manualNumber', manual ? `${SUMMARY}.manualNumber` : `${SUMMARY}.manualNumberEmpty`, manual ? { value: shorten(manual) } : undefined);
  }
  if (context.accountManager && header.accountManagerId) add('accountManager', `${SUMMARY}.accountManager`);
  if (context.customerPayments && header.customerPaymentNotes.trim()) add('customerPaymentNotes', `${SUMMARY}.customerPaymentNotes`);
  if (context.commercial && header.supplierId) add('supplier', `${SUMMARY}.supplier`);
  if (context.commercial && header.factoryLocation.trim()) add('factoryLocation', `${SUMMARY}.factoryLocation`);
  if (context.isNew && header.notifyUsers.length > 0) add('notifyUsers', `${SUMMARY}.notifyUsers`, { count: header.notifyUsers.length });
  return tokens;
}

/** The sections of the second level „Zaawansowane”, in screen order. */
export type OrderDraftMoreSectionId = 'numbering' | 'payment' | 'supplier' | 'notify';

/** Sections that have at least one field for this role (a section without a field is not rendered). */
export function getOrderDraftMoreSections(
  context: Pick<OrderDraftMoreSummaryContext, 'isNew' | 'commercial' | 'customerPayments'>,
): OrderDraftMoreSectionId[] {
  const sections: OrderDraftMoreSectionId[] = ['numbering'];
  if (context.customerPayments) sections.push('payment');
  if (context.commercial) sections.push('supplier');
  if (context.isNew) sections.push('notify');
  return sections;
}

/** The section that holds the field a save problem (an `orders:` key of `getOrderDraftSaveProblem`) is about. */
export function getOrderDraftMoreSectionForProblem(problemKey: string | null | undefined): OrderDraftMoreSectionId | null {
  if (problemKey === 'orders:modal.validation.manualNumberRequired' || problemKey === 'orders:process.draft.errors.manualNumber') {
    return 'numbering';
  }
  return null;
}

/** A save problem about a field of „Więcej danych” itself (outside „Zaawansowane”). */
export function isOrderDraftMoreFieldProblem(problemKey: string | null | undefined): boolean {
  return problemKey === 'orders:process.draft.errors.prepaymentPercent';
}

/** „Jan Kowalski” → „Jan K.” */
export function shortPersonName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return parts[0] || '';
  return `${parts[0]} ${parts[parts.length - 1].charAt(0).toUpperCase()}.`;
}

/**
 * The short value summary on the header of each section of „Zaawansowane”
 * (e.g. „Auto · opiekun: Jan K.”); an empty list means nothing is set. The
 * names come from the option lists, which load only while a section is open
 * or holds a value — until then a generic marker without the name is used.
 */
export function getOrderDraftMoreSectionSummaries(
  draft: OrderDraftState,
  context: OrderDraftMoreSummaryContext & { accountManagerName?: string; supplierName?: string },
): Record<OrderDraftMoreSectionId, OrderDraftMoreSummaryToken[]> {
  const { header } = draft;
  const now = context.now ?? dayjs();
  const result: Record<OrderDraftMoreSectionId, OrderDraftMoreSummaryToken[]> = {
    numbering: [], payment: [], supplier: [], notify: [],
  };
  const add = (section: OrderDraftMoreSectionId, id: string, key: string, params?: Record<string, string | number>) => {
    result[section].push({ id, key, params });
  };

  if (header.useManualNumbering) {
    const manual = header.manualOrderNumber.trim();
    add('numbering', 'manualNumber', manual ? `${SUMMARY}.manualNumber` : `${SUMMARY}.manualNumberEmpty`, manual ? { value: shorten(manual) } : undefined);
  } else {
    add('numbering', 'autoNumber', `${SUMMARY}.autoNumber`);
  }
  if (header.orderDate?.isValid() && !header.orderDate.isSame(now, 'day')) {
    add('numbering', 'orderDate', `${SUMMARY}.orderDate`, { date: shortDate(header.orderDate, now) });
  }
  if (context.accountManager && header.accountManagerId) {
    if (context.accountManagerName) add('numbering', 'accountManager', `${SUMMARY}.accountManagerNamed`, { name: shortPersonName(context.accountManagerName) });
    else add('numbering', 'accountManager', `${SUMMARY}.accountManager`);
  }
  if (context.customerPayments && header.customerPaymentNotes.trim()) {
    add('payment', 'customerPaymentNotes', `${SUMMARY}.paymentNotes`);
  }
  if (context.commercial && header.supplierId) {
    if (context.supplierName) add('supplier', 'supplier', `${SUMMARY}.supplierNamed`, { name: shorten(context.supplierName) });
    else add('supplier', 'supplier', `${SUMMARY}.supplier`);
  }
  if (context.commercial && header.factoryLocation.trim()) {
    add('supplier', 'factoryLocation', `${SUMMARY}.factoryLocationNamed`, { value: shorten(header.factoryLocation.trim()) });
  }
  if (context.isNew && header.notifyUsers.length > 0) add('notify', 'notifyUsers', `${SUMMARY}.notifyRecipients`, { count: header.notifyUsers.length });
  return result;
}
